import { crmApiFetch } from "../../services/crmApi";
import { callCoreApi } from "../../services/coreApi";

/**
 * Get all team members in the organization
 * @returns {Promise<Array>} Array of team members with id, name, email, role
 */
export async function getTeamMembers() {
	const response = await crmApiFetch(`/api/contacts/team-members`, {
		method: "GET",
	});

	let data = {};
	try {
		data = await response.json();
	} catch {
		data = {};
	}

	if (!response.ok) {
		throw new Error(data.error || "Failed to fetch team members");
	}

	return data.team_members || [];
}

/**
 * Create a new meeting
 * @param {Object} meetingData - The meeting details
 * @param {string} meetingData.subject - Meeting subject/title
 * @param {string} [meetingData.description] - Meeting description
 * @param {Date} meetingData.startTime - Start time
 * @param {Date} meetingData.endTime - End time
 * @param {string} meetingData.timeZone - Timezone (e.g., "Eastern Standard Time")
 * @param {string} [meetingData.location] - Physical location
 * @param {boolean} [meetingData.isTeamsOnline] - Enable Teams meeting
 * @param {string[]} [meetingData.attendees] - Email addresses of attendees
 * @param {number} [meetingData.dealId] - Associated deal ID
 * @param {number} [meetingData.contactId] - Associated contact ID
 * @param {number} [meetingData.reminderMinutes] - Reminder time in minutes
 * @returns {Promise<Object>} Meeting response with ID, join URL, etc.
 */
export async function createMeeting(meetingData) {
	const payload = {
		subject: meetingData.subject,
		description: meetingData.description || "",
		start_time: meetingData.startTime.toISOString(),
		end_time: meetingData.endTime.toISOString(),
		time_zone: meetingData.timeZone,
		location: meetingData.location || "",
		is_teams_online: meetingData.isTeamsOnline || false,
		attendees: meetingData.attendees || [],
		deal_id: meetingData.dealId || undefined,
		account_id: meetingData.accountId || undefined,
		contact_id: meetingData.contactId || undefined,
		reminder_minutes: meetingData.reminderMinutes || 15,
	};

	// Remove undefined fields
	Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);

	const data = await callCoreApi(`/api/meetings`, { method: "POST", body: payload });
	return data.meeting;
}

/**
 * Get all meetings for the authenticated user
 * @param {Object} [options] - Filter options
 * @param {number} [options.dealId] - Filter by deal ID
 * @param {number} [options.contactId] - Filter by contact ID
 * @returns {Promise<Array>} Array of meetings
 */
export async function getMeetings(options = {}) {
	const searchParams = new URLSearchParams();
	if (options.dealId) {
		searchParams.set("deal_id", String(options.dealId));
	}
	if (options.contactId) {
		searchParams.set("contact_id", String(options.contactId));
	}

	const data = await callCoreApi(`/api/meetings?${searchParams.toString()}`);
	return data.meetings || [];
}

/**
 * Get a specific meeting by ID
 * @param {number} meetingId - The meeting ID
 * @returns {Promise<Object>} Meeting details
 */
export async function getMeetingByID(meetingId) {
	const data = await callCoreApi(`/api/meetings/${meetingId}`);
	return data.meeting;
}

export async function updateMeetingOutcome(meetingId, { status, outcome }) {
	const data = await callCoreApi(`/api/meetings/${meetingId}/outcome`, {
		method: "PATCH",
		body: { status, outcome: outcome || "" },
	});
	return data.meeting;
}

/**
 * Cancel a meeting
 * @param {number} meetingId - The meeting ID to cancel
 * @returns {Promise<Object>} Response confirmation
 */
export async function cancelMeeting(meetingId) {
	return callCoreApi(`/api/meetings/${meetingId}/cancel`, { method: "POST" });
}

/**
 * Query teammate availability and get suggested meeting times
 * @param {Object} options
 * @param {string[]} options.teamEmails - Org member emails to check
 * @param {string} options.startDate - Start date YYYY-MM-DD
 * @param {number} options.meetingDurationMins - Desired meeting length
 * @param {string} options.timeZone - Timezone string
 * @param {string} [options.workingHoursStart] - e.g. "09:00"
 * @param {string} [options.workingHoursEnd]   - e.g. "18:00"
 * @returns {Promise<{busySlots: Array, suggestedSlots: Array, timeZone: string}>}
 */
export async function queryAvailability(options) {
	const payload = {
		team_emails: options.teamEmails,
		start_date: options.startDate,
		meeting_duration_mins: options.meetingDurationMins,
		time_zone: options.timeZone,
		working_hours_start: options.workingHoursStart || "09:00",
		working_hours_end: options.workingHoursEnd || "18:00",
	};

	const data = await callCoreApi(`/api/availability/query`, { method: "POST", body: payload });
	return {
		busySlots: data.busy_slots || [],
		suggestedSlots: data.suggested_slots || [],
		timeZone: data.time_zone || options.timeZone,
		queriedEmails: data.queried_emails || [],
	};
}
